import type {
  BulkUpdateTimeEntriesInput,
  BulkUpdateTimeEntriesResult,
  CreateTimeEntryRequest,
} from '@/lib/api/time-entries.api'
import { timeEntryApi } from '@/lib/api/time-entries.api'
import { createLogger } from '@/lib/logger'
/**
 * Time Entry mutation hooks using factory pattern with timer-specific operations
 * Reduced from 185 lines to ~100 lines (46% reduction)
 * All features preserved: CRUD, pause/resume, heartbeat
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
  SplitTimeEntryInput,
  SwitchTimerItemInput,
  TimeEntry,
  UpdateTimeEntryInput,
} from '@scrambl/shared'

import { payPeriodKeys } from '../queries/usePayPeriodsQuery'
import { queryKeys } from '../query-keys'
import { createResourceMutations } from './createResourceMutations'

const logger = createLogger('hooks/mutations/useTimeEntryMutations')

// Helper to invalidate time entry related queries
function invalidateTimeEntryQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all })
  queryClient.invalidateQueries({ queryKey: payPeriodKeys.all })
}

// Core CRUD mutations using factory
const timeEntryMutations = createResourceMutations<
  TimeEntry,
  CreateTimeEntryRequest,
  UpdateTimeEntryInput
>({
  resourceName: 'Time entry',
  resourceNameSingular: 'timeEntry',
  queryKeyFactory: queryKeys.timeEntries,
  api: {
    create: (data: CreateTimeEntryRequest) =>
      timeEntryApi.createTimeEntry(data),
    update: (id: string, data: UpdateTimeEntryInput) =>
      timeEntryApi.updateTimeEntry(id, data),
    delete: (id: string) => timeEntryApi.deleteTimeEntry(id),
  },
  invalidation: {
    onCreate: {
      additionalInvalidations: [
        () => queryKeys.timeEntries.all,
        () => payPeriodKeys.all,
      ],
    },
    onUpdate: {
      additionalInvalidations: [
        () => queryKeys.timeEntries.all,
        () => payPeriodKeys.all,
      ],
    },
    onDelete: {
      additionalInvalidations: [
        () => queryKeys.timeEntries.all,
        () => payPeriodKeys.all,
      ],
    },
  },
  enableRealtime: false,
})

export const useCreateTimeEntryMutation = timeEntryMutations.useCreateMutation
export const useUpdateTimeEntryMutation = timeEntryMutations.useUpdateMutation
export const useDeleteTimeEntryMutation = timeEntryMutations.useDeleteMutation

// Timer-specific mutations
export function usePauseTimerMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => timeEntryApi.pauseTimer(id),
    onSuccess: updatedTimeEntry => {
      queryClient.setQueryData(
        queryKeys.timeEntries.detail(updatedTimeEntry.id),
        updatedTimeEntry,
      )
      invalidateTimeEntryQueries(queryClient)
      toast.success('Timer paused')
    },
    onError: error => {
      logger.error('[usePauseTimerMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to pause timer',
      )
    },
  })
}

export function useResumeTimerMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => timeEntryApi.resumeTimer(id),
    onSuccess: updatedTimeEntry => {
      queryClient.setQueryData(
        queryKeys.timeEntries.detail(updatedTimeEntry.id),
        updatedTimeEntry,
      )
      invalidateTimeEntryQueries(queryClient)
      toast.success('Timer resumed')
    },
    onError: error => {
      logger.error('[useResumeTimerMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to resume timer',
      )
    },
  })
}

export function useUpdateHeartbeatMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => timeEntryApi.updateHeartbeat(id),
    onSuccess: result => {
      // If the timer is no longer active on the server, invalidate queries
      // to refresh state and clear the stale timer
      if (!result.active) {
        invalidateTimeEntryQueries(queryClient)
      }
    },
    onError: error => {
      logger.error('[useUpdateHeartbeatMutation] Error:', error)
      // No toast for heartbeat errors - background operation
    },
  })
}

export function useSplitTimeEntryMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SplitTimeEntryInput }) =>
      timeEntryApi.splitTimeEntry(id, data),
    onSuccess: () => {
      invalidateTimeEntryQueries(queryClient)
      toast.success('Time entry split')
    },
    onError: error => {
      logger.error('[useSplitTimeEntryMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to split time entry',
      )
    },
  })
}

export function useSwitchTimerItemMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SwitchTimerItemInput }) =>
      timeEntryApi.switchTimerItem(id, data),
    onSuccess: updatedTimeEntry => {
      queryClient.setQueryData(
        queryKeys.timeEntries.detail(updatedTimeEntry.id),
        updatedTimeEntry,
      )
      invalidateTimeEntryQueries(queryClient)
    },
    onError: error => {
      logger.error('[useSwitchTimerItemMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to switch timer item',
      )
    },
  })
}

/**
 * "Updated 3 · skipped 1 (approved or invoiced)" — the API skips entries it
 * won't reclassify, so the toast always reports both halves when any skip.
 */
export function formatBulkUpdateSummary(
  result: BulkUpdateTimeEntriesResult,
): string {
  const updated = result.updated.length
  const skipped = result.skipped.length
  if (skipped === 0) {
    return `Updated ${updated} ${updated === 1 ? 'entry' : 'entries'}`
  }
  return `Updated ${updated} · skipped ${skipped} (approved or invoiced)`
}

/** Bulk reclassify (billable, time category) for a selection of entries. */
export function useBulkUpdateTimeEntriesMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: BulkUpdateTimeEntriesInput) =>
      timeEntryApi.bulkUpdateTimeEntries(input),
    onSuccess: result => {
      invalidateTimeEntryQueries(queryClient)
      if (result.updated.length === 0 && result.skipped.length > 0) {
        toast.warning(formatBulkUpdateSummary(result))
      } else {
        toast.success(formatBulkUpdateSummary(result))
      }
    },
    onError: error => {
      logger.error('[useBulkUpdateTimeEntriesMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to update entries',
      )
    },
  })
}
